import { tokenManager } from '@/lib/auth/token-manager';
import { env } from '@/lib/config/env';
import { logError } from '@/lib/logging';
import { z } from 'zod';

interface ApiErrorResponse {
  message?: string;
}

function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
  if (typeof value !== 'object' || value === null) return false;
  if (!('message' in value)) return false;
  const messageValue = (value as { message: unknown }).message;
  return messageValue === undefined || typeof messageValue === 'string';
}

export class ApiValidationError extends Error {
  public readonly endpoint: string;
  public readonly validationIssues: z.core.$ZodIssue[];

  constructor(message: string, endpoint: string, issues: z.core.$ZodIssue[]) {
    super(message);
    this.name = 'ApiValidationError';
    this.endpoint = endpoint;
    this.validationIssues = issues;
  }
}

export class ApiHttpError extends Error {
  public readonly statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'ApiHttpError';
    this.statusCode = statusCode;
  }
}

function headersInitToRecord(headers: HeadersInit): Record<string, string> {
  if (headers instanceof Headers) {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }
  if (Array.isArray(headers)) {
    const result: Record<string, string> = {};
    for (const [key, value] of headers) {
      result[key] = value;
    }
    return result;
  }
  return headers;
}

function mergeHeaders(
  baseHeaders: Record<string, string>,
  additionalHeaders?: HeadersInit
): Record<string, string> {
  if (!additionalHeaders) return baseHeaders;
  return { ...baseHeaders, ...headersInitToRecord(additionalHeaders) };
}

function addAuthorizationHeader(
  headers: Record<string, string>,
  token: string | null
): Record<string, string> {
  if (token) {
    return { ...headers, Authorization: `Bearer ${token}` };
  }
  return headers;
}

async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const contentType = response.headers.get('content-type');

    if (contentType && contentType.includes('application/json')) {
      const errorData: unknown = await response.json();
      if (isApiErrorResponse(errorData) && errorData.message) {
        return errorData.message;
      }

      return `HTTP ${String(response.status)}`;
    }

    const errorText = await response.text();
    if (errorText.trim() !== '') {
      return errorText;
    }

    return `HTTP ${String(response.status)}: ${response.statusText}`;
  } catch (parseError) {
    logError(parseError, {
      operation: 'extractErrorMessage',
      metadata: {
        status: response.status,
        statusText: response.statusText,
      },
    });
    return `HTTP ${String(response.status)}: ${response.statusText}`;
  }
}

function isUnauthorizedResponse(status: number): boolean {
  return status === 401;
}

function redirectToLoginAfterUnauthorized(): never {
  tokenManager.clearToken();
  window.location.href = '/login';
  throw new Error('Unauthorized - redirecting to login');
}

function isNoContentResponse(status: number): boolean {
  return status === 204;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async handleResponse<T>(
    response: Response,
    endpoint: string,
    schema?: z.ZodType<T>
  ): Promise<T> {
    if (!response.ok) {
      if (isUnauthorizedResponse(response.status)) {
        redirectToLoginAfterUnauthorized();
      }
      const errorMessage = await extractErrorMessage(response);
      throw new ApiHttpError(errorMessage, response.status);
    }

    if (isNoContentResponse(response.status)) {
      const emptyResult = {};
      if (schema) {
        return schema.parse(emptyResult);
      }
      return emptyResult as T;
    }

    const data: unknown = await response.json();

    if (schema) {
      try {
        return schema.parse(data);
      } catch (error) {
        if (error instanceof z.ZodError) {
          logError(error, {
            operation: 'apiClient.handleResponse',
            metadata: {
              endpoint,
              validationError: error.issues,
            },
          });
          throw new ApiValidationError('API response validation failed', endpoint, error.issues);
        }
        throw error;
      }
    }

    return data as T;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    schema?: z.ZodType<T>
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = tokenManager.getToken();

    const baseHeaders = mergeHeaders({ 'Content-Type': 'application/json' }, options.headers);
    const headers = addAuthorizationHeader(baseHeaders, token);

    const response = await fetch(url, { ...options, headers, credentials: 'include' });

    return this.handleResponse<T>(response, endpoint, schema);
  }

  async get<T>(endpoint: string, schema?: z.ZodType<T>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' }, schema);
  }

  async post<T>(endpoint: string, data?: unknown, schema?: z.ZodType<T>): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'POST',
        ...(data !== undefined && { body: JSON.stringify(data) }),
      },
      schema
    );
  }

  async postFormData<T>(endpoint: string, formData: FormData, schema?: z.ZodType<T>): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = tokenManager.getToken();
    const headers = addAuthorizationHeader({}, token);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    return this.handleResponse<T>(response, endpoint, schema);
  }

  async put<T>(endpoint: string, data: unknown, schema?: z.ZodType<T>): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      schema
    );
  }

  async patch<T>(endpoint: string, data: unknown, schema?: z.ZodType<T>): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      schema
    );
  }

  async delete<T>(endpoint: string, schema?: z.ZodType<T>): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' }, schema);
  }
}

export const apiClient = new ApiClient(env.apiBaseUrl);
